import type { PDFDocumentProxy } from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.min.mjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import BBoxOverlay from './BBoxOverlay';
import OCRTextOverlay from './OCRTextOverlay';
import { PdfViewer } from './PdfCuration.style';
import type { OCRData, FHIRResource } from './types';

type Props = {
  pdf: PDFDocumentProxy | null;
  ocrData: OCRData | null;
  bboxData: FHIRResource[] | null;
  isLoading: boolean;
  setBboxData: React.Dispatch<React.SetStateAction<FHIRResource[] | null>>;
  setOcrData: React.Dispatch<React.SetStateAction<OCRData | null>>;
  activeScrollPage: number | null;
};

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

const clampZoom = (z: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

const PDFViewer: React.FC<Props> = ({
  pdf,
  ocrData,
  bboxData,
  isLoading,
  setBboxData,
  setOcrData,
  activeScrollPage,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pageContainerRefs = useRef<Record<number, HTMLDivElement | null>>({});

  //
  // Set numPages when PDF changes
  //
  useEffect(() => {
    if (!pdf) {
      setNumPages(0);
      return;
    }
    setNumPages(pdf.numPages);
  }, [pdf]);

  //
  // Scroll to activeScrollPage when it changes
  //
  useEffect(() => {
    if (!activeScrollPage || !containerRef.current) return;

    const targetEl = pageContainerRefs.current[activeScrollPage];
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeScrollPage]);

  //
  // Pinch-to-zoom via trackpad (Ctrl/⌘ + wheel)
  //
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();

      const { deltaY } = e;
      const zoomFactor = deltaY < 0 ? 1.05 : 0.95;

      setZoom((prev) => clampZoom(prev * zoomFactor));
    }
  }, []);

  //
  // No PDF: simple fallback
  //
  if (!pdf) {
    return (
      <PdfViewer>{isLoading ? 'Loading PDF…' : 'No PDF loaded.'}</PdfViewer>
    );
  }

  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <PdfViewer
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
      }}
      onWheel={handleWheel}
    >
      {pages.map((pageNumber) => (
        <PageWithOverlays
          key={pageNumber}
          pdf={pdf}
          pageNumber={pageNumber}
          ocrData={ocrData}
          bboxData={bboxData}
          setBboxData={setBboxData}
          setOcrData={setOcrData}
          registerContainerRef={(el) => {
            pageContainerRefs.current[pageNumber] = el;
          }}
          zoom={zoom}
        />
      ))}
    </PdfViewer>
  );
};

//
// --------------------------------------------------------
// PAGE COMPONENT — geometry + overlays + CSS zoom
// --------------------------------------------------------
//

type PageWithOverlaysProps = {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  ocrData: OCRData | null;
  bboxData: FHIRResource[] | null;
  setBboxData: React.Dispatch<React.SetStateAction<FHIRResource[] | null>>;
  setOcrData: React.Dispatch<React.SetStateAction<OCRData | null>>;
  registerContainerRef: (el: HTMLDivElement | null) => void;
  zoom: number;
};

const PageWithOverlays: React.FC<PageWithOverlaysProps> = ({
  pdf,
  pageNumber,
  ocrData,
  bboxData,
  setBboxData,
  setOcrData,
  registerContainerRef,
  zoom,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [canvasRect, setCanvasRect] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const [pageReady, setPageReady] = useState(false);

  const pageKey = `Page_${pageNumber}`;
  const ocrPage = ocrData?.[pageKey] || null;

  const [originalWidth, setOriginalWidth] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);

  //
  // Measure canvas rect in layout (unzoomed) coordinates.
  // Use offsetWidth/offsetHeight so it does NOT change with CSS scale().
  //
  const updateCanvasRect = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.offsetWidth || canvas.width;
    const height = canvas.offsetHeight || canvas.height;

    if (!width || !height) return;

    setCanvasRect({
      width,
      height,
    });
  }, []);

  //
  // Render PAGE into canvas, once per (pdf, pageNumber, ocrPage).
  // No zoom dependency here → no multi-render error.
  //
  useEffect(() => {
    let cancelled = false;
    let renderTask: any = null;

    const render = async () => {
      if (!canvasRef.current) return;
      const page = await pdf.getPage(pageNumber);

      const baseViewport = page.getViewport({ scale: 1 });

      const fallbackWidth = ocrPage?.width ?? baseViewport.width;
      const fallbackHeight = ocrPage?.height ?? baseViewport.height;

      if (!cancelled) {
        setOriginalWidth(fallbackWidth);
        setOriginalHeight(fallbackHeight);
      }

      const baseScale = fallbackWidth / baseViewport.width;
      const viewport = page.getViewport({ scale: baseScale });

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const pixelRatio = window.devicePixelRatio || 1;

      canvas.width = Math.round(viewport.width * pixelRatio);
      canvas.height = Math.round(viewport.height * pixelRatio);

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      renderTask = page.render({ canvasContext: ctx, viewport });

      try {
        await renderTask.promise;
      } catch (err: any) {
        if (err && err.name === 'RenderingCancelledException') {
          return;
        }
        throw err;
      }

      if (!cancelled) {
        updateCanvasRect();
        setPageReady(true);
      }
    };

    render();

    return () => {
      cancelled = true;
      if (renderTask) {
        try {
          renderTask.cancel();
        } catch {
          // ignore
        }
      }
    };
  }, [pdf, pageNumber, ocrPage, updateCanvasRect]);

  //
  // Keep geometry updated on window resize
  //
  useEffect(() => {
    const handleResize = () => updateCanvasRect();
    window.addEventListener('resize', handleResize);

    updateCanvasRect();
    return () => window.removeEventListener('resize', handleResize);
  }, [updateCanvasRect]);

  const canRenderOverlays =
    pageReady && canvasRect && originalWidth > 0 && originalHeight > 0;

  return (
    <div
      ref={registerContainerRef}
      style={{
        position: 'relative',
        marginBottom: 16,
        width: '100%',
        overflow: 'visible',
      }}
    >
      {/* Inner wrapper gets zoom applied (canvas + overlays together) */}
      <div
        style={{
          position: 'relative',
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
        }}
      >
        {/* PDF CANVAS */}
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
          }}
        />

        {/* OCR + BBOX overlays */}
        {canRenderOverlays && (
          <>
            {/* OCR selectable text */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'auto',
              }}
            >
              <OCRTextOverlay
                ocrData={ocrData}
                currentPage={pageNumber}
                canvasRef={canvasRef}
                originalWidth={originalWidth}
                originalHeight={originalHeight}
                setOcrData={setOcrData}
                canvasRect={canvasRect}
              />
            </div>

            {/* BOUNDING BOXES */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
              }}
            >
              <BBoxOverlay
                bboxData={bboxData}
                ocrData={ocrData}
                currentPage={pageNumber}
                originalWidth={originalWidth}
                originalHeight={originalHeight}
                setBboxData={setBboxData}
                canvasRect={canvasRect}
                zoom={zoom}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;
